import type { ListeTableFilterData } from '@mairistem/liste';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';

import type { ApiResponseType } from '@/types';

interface UseListeSearchSortProps {
  // Récupère tous les éléments au lieu de page par page
  all?: boolean;
  active: boolean;
  getItems: (props: Record<string, any>) => string;
  searchItems?: (props: Record<string, any>) => string;
  /** Paramètres fixe à ajouter à la route (search ou index)
   * Sert notamment pour éviter de recréer la fonction getItems à chaque rendu
   * dans un cas comme (code) => (paramsUseListeSearchSort) => '/api/endpoint?code=${code}'
   */
  fixedParams?: Record<string, any>;
}

const useListeSearchSort = (props: UseListeSearchSortProps) => {
  const { active, getItems, searchItems, all = false, fixedParams = null } = props;

  const [items, setItems] = React.useState<Record<string, any>[]>();
  const [filters, setFilters] = React.useState<ListeTableFilterData[]>();
  const [sorts, setSorts] = React.useState<Record<string, 'asc' | 'desc'>>();
  const [total, setTotal] = React.useState<number>(0);
  const [page, setPage] = React.useState<number>(1);
  const [search, setSearch] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);

  /** Permet d'annuler toutes les requêtes HTTP obsolètes au besoin. */
  const abortControllers = React.useRef<AbortController[]>([]);
  /** Pour éviter d'avoir à disable le talbeau de dép de getRoute */
  const fixedParamsRef = React.useRef<Record<string, any> | null>(fixedParams);
  fixedParamsRef.current = fixedParams;

  /** Annule toutes les requêtes HTTP présentes dans abortControllers. */
  const cancelAllRequest = React.useCallback(() => {
    abortControllers.current.forEach((controller) => controller.abort());
    abortControllers.current = [];
  }, []);

  const getRoute = React.useCallback(
    (p = 1, otherParams = {}) => {
      const route =
        search !== ''
          ? searchItems({
              page: p,
              query: search,
              sorts,
              filters,
              ...(fixedParamsRef.current || null),
              ...otherParams,
            })
          : getItems({
              page: p,
              sorts,
              filters,
              ...(fixedParamsRef.current || null),
              ...otherParams,
            });

      return route;
    },
    [filters, getItems, search, searchItems, sorts],
  );

  const loadItems = React.useCallback(
    (p = 1, otherParams = {}) => {
      if (p === 1) {
        cancelAllRequest();

        setLoading(true);
      }

      const route = getRoute(p, otherParams);

      const controller = new AbortController();
      abortControllers.current.push(controller);

      request
        .get(route, { signal: controller.signal })
        .then(({ data, meta: { total: t, lastPage } }: ApiResponseType<Record<string, any>>) => {
          const pendingPromises = [];

          for (let i = 2; i < (all ? lastPage + 1 : 1); i += 1) {
            // then pour ne garder que les data des réponses dans le Promise.all
            pendingPromises.push(request.get(getRoute(i)).then(({ data: d }) => d));
          }

          Promise.all(pendingPromises)
            .then((responses) => {
              if (all) {
                setItems([...data, ..._.flatten(responses)]);
                setPage(lastPage);
                setTotal(t);
              } else {
                if (p === 1) {
                  setItems(data);
                } else {
                  setItems((prev) => _.uniqBy([...(prev || []), ...data], 'identifiant'));
                }

                setPage(p);

                if (p === 1) {
                  setTotal(t);
                }
              }
            })
            .finally(() => {
              if (p === 1 || all) {
                setLoading(false);
              }
            });
        });
    },
    [getRoute, cancelAllRequest, all],
  );

  React.useEffect(() => {
    if (active && (!all || page === 1)) {
      loadItems(page);
    }
  }, [page, active, loadItems, search, filters, all]);

  return {
    loading,
    items,
    setItems,
    filters,
    setFilters,
    sorts,
    setSorts,
    total,
    setTotal,
    page,
    setPage,
    search,
    setSearch,
    reset: loadItems,
  };
};

export default useListeSearchSort;
